import React, { useEffect, useMemo, useState, useRef } from 'react';
import UserDetailView from './UserDetailView';
import FoodPhotoReview from './FoodPhotoReview';
import CoachRulesModal from './CoachRulesModal';
import { BACKEND_URL } from '../config';

function AdminDashboard({
  adminUsers,
  selectedUser,
  onViewDetails,
  onBack,
  token,
  adminUserId,
  onSendMessage,
  onCreateGoal,
  onRefreshUsers,
  showToast
}) {
  const [showFoodReview, setShowFoodReview] = useState(false);

  // ✅ Reusable Coach Rules modal state
  const [showCoachRules, setShowCoachRules] = useState(false);
  const [coachRulesUser, setCoachRulesUser] = useState(null);

  // Trainer management state
  const [trainers, setTrainers] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignTrainerId, setAssignTrainerId] = useState('');
  const [assignClientIds, setAssignClientIds] = useState([]);
  const [uploadingWorkouts, setUploadingWorkouts] = useState(false);
  const [uploadingRecipes, setUploadingRecipes] = useState(false);
  const workoutsFileRef = useRef(null);
  const recipesFileRef = useRef(null);

  const uploadWorkoutsCSV = async (file) => {
    if (!file) return;
    setUploadingWorkouts(true);
    try {
      const fd = new FormData();
      fd.append("file", file);

      const res = await fetch(`${BACKEND_URL}/api/workouts/library/import-csv`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`, // make sure AdminDashboard receives token prop
        },
        body: fd,
      });

      const text = await res.text();
      let data = null;
      try { data = text ? JSON.parse(text) : null; } catch { data = { detail: text }; }

      if (!res.ok) throw new Error(data?.detail || text || `HTTP ${res.status}`);

      showToast?.(`✅ Workouts imported! Inserted: ${data?.inserted ?? "?"}`);
    } catch (e) {
      showToast?.("❌ Upload failed: " + (e?.message || String(e)), "error");
    } finally {
      setUploadingWorkouts(false);
    }
  };

  const uploadRecipesCSV = async (file) => {
    if (!file) return;
    setUploadingRecipes(true);
    try {
      const fd = new FormData();
      fd.append("file", file);

      const res = await fetch(`${BACKEND_URL}/api/nutrition/library/import-csv`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: fd,
      });

      const text = await res.text();
      let data = null;
      try { data = text ? JSON.parse(text) : null; } catch { data = { detail: text }; }

      if (!res.ok) throw new Error(data?.detail || text || `HTTP ${res.status}`);

      showToast?.(`✅ Recipes imported! Rows: ${data?.inserted ?? "?"}, Loaded: ${data?.loaded_count ?? "?"}`);
    } catch (e) {
      showToast?.("❌ Recipe upload failed: " + (e?.message || String(e)), "error");
    } finally {
      setUploadingRecipes(false);
    }
  };

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);

  const jsonHeaders = useMemo(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  }), [token]);

  const pendingUsers = useMemo(
    () => (adminUsers || []).filter(u => u?.auth_info?.approved === false),
    [adminUsers]
  );

  const approvedUsers = useMemo(
    () => (adminUsers || []).filter(u => u?.auth_info?.approved !== false),
    [adminUsers]
  );

  const clientCandidates = useMemo(() => {
    return (adminUsers || [])
      .map(u => u?.auth_info)
      .filter(u => u && u.user_id && !u.is_admin);
  }, [adminUsers]);

  const isTrainerUser = (u) => !!u?.auth_info?.is_trainer;

  // =======================
  // Coach Rules open/close
  // =======================
  const openCoachRules = (user) => {
    setCoachRulesUser(user);
    setShowCoachRules(true);
  };

  const closeCoachRules = () => {
    setShowCoachRules(false);
    setCoachRulesUser(null);
  };

  // =======================
  // Trainer management API
  // =======================
  const loadTrainers = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/trainers`, { headers: authHeaders });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setTrainers(data.trainers || []);
    } catch (e) {
      console.warn('Could not load trainers:', e.message || e);
      setTrainers([]);
    }
  };
  
  const makeTrainer = async (userId) => {
    if (!window.confirm('Make this user a Trainer?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/make-trainer/${userId}`, {
        method: 'POST',
        headers: authHeaders
      });
      if (!res.ok) throw new Error((await res.text()) || 'Failed to make trainer');
      showToast?.('Trainer role granted!');
      await loadTrainers();
      onRefreshUsers?.();
    } catch (e) {
      showToast?.('Error: ' + (e.message || 'Failed'), "error");
    }
  };

  const revokeTrainer = async (userId) => {
    if (!window.confirm('Remove Trainer role from this user?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/revoke-trainer/${userId}`, {
        method: 'POST',
        headers: authHeaders
      });
      if (!res.ok) throw new Error((await res.text()) || 'Failed to revoke trainer');
      showToast?.('Trainer role removed.');
      await loadTrainers();
      onRefreshUsers?.();
    } catch (e) {
      showToast?.('Error: ' + (e.message || 'Failed'), "error");
    }
  };

  const openAssignClients = async () => {
    setShowAssignModal(true);
    setAssignTrainerId('');
    setAssignClientIds([]);
    setAssignLoading(true);
    try {
      await loadTrainers();
    } finally {
      setAssignLoading(false);
    }
  };

  const closeAssignClients = () => {
    if (assignLoading) return;
    setShowAssignModal(false);
  };

  const loadAssignmentsForTrainer = async (trainerId) => {
    if (!trainerId) return;
    setAssignLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/trainers/${trainerId}/assignments`, { headers: authHeaders });
      if (!res.ok) throw new Error((await res.text()) || 'Failed to load assignments');
      const data = await res.json();
      setAssignClientIds(data.client_ids || []);
    } catch (e) {
      showToast?.('Error loading assignments: ' + (e.message || 'Failed'), "error");
      setAssignClientIds([]);
    } finally {
      setAssignLoading(false);
    }
  };

  const saveAssignmentsForTrainer = async () => {
    if (!assignTrainerId) {
      showToast?.('Pick a trainer first.', "error");
      return;
    }
    setAssignLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/trainers/${assignTrainerId}/assignments`, {
        method: 'PUT',
        headers: jsonHeaders,
        body: JSON.stringify({ client_ids: assignClientIds })
      });
      if (!res.ok) throw new Error((await res.text()) || 'Failed to save assignments');
      showToast?.('Assignments saved!');
      closeAssignClients();
    } catch (e) {
      showToast?.('Error saving assignments: ' + (e.message || 'Failed'), "error");
    } finally {
      setAssignLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadTrainers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // =======================
  // Early returns
  // =======================
  if (showFoodReview) {
    return <FoodPhotoReview token={token} onBack={() => setShowFoodReview(false)} />;
  }

  if (selectedUser) {
    return (
      <UserDetailView
        user={selectedUser}
        onBack={onBack}
        token={token}
        adminUserId={adminUserId}
        onSendMessage={onSendMessage}
        onCreateGoal={onCreateGoal}
        onRefreshUsers={onRefreshUsers}
        showToast={showToast}
      />
    );
  }

  // =======================
  // Render
  // =======================
  return (
    <div className="admin-view">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 className="heading-2">Admin Dashboard</h1>
          <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
            Total Users: {adminUsers.length} ({pendingUsers.length} pending approval)
          </p>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <input
              ref={workoutsFileRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                uploadWorkoutsCSV(file);
              }}
            />

            <input
              ref={recipesFileRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                uploadRecipesCSV(file);
              }}
            />

            <button
              type="button"
              className="btn-primary"
              disabled={uploadingWorkouts}
              onClick={() => workoutsFileRef.current?.click()}
            >
              {uploadingWorkouts ? "Uploading…" : "Upload Workouts CSV"}
            </button>

            <button
              type="button"
              className="btn-primary"
              disabled={uploadingRecipes}
              onClick={() => recipesFileRef.current?.click()}
            >
              {uploadingRecipes ? "Uploading…" : "Upload Recipes CSV"}
            </button>
          </div>
          <button
            onClick={() => setShowFoodReview(true)}
            className="btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            data-testid="review-food-photos-btn"
          >
            📸 Review Food Photos
          </button>

          <button
            onClick={openAssignClients}
            className="btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            🧑‍🏫 Assign Clients
          </button>
        </div>
      </div>

      {/* Pending Approval Section */}
      {pendingUsers.length > 0 && (
        <>
          <h2 className="heading-3" style={{ marginBottom: 16, color: '#92400e' }}>
            ⏳ Pending Approval ({pendingUsers.length})
          </h2>

          <div className="admin-users-grid" style={{ marginBottom: 32 }}>
            {pendingUsers.map(user => (
              <div key={user.auth_info.user_id} className="admin-user-card" style={{ borderColor: '#fbbf24', borderWidth: 2 }}>
                <div className="admin-user-header">
                  <h3 className="heading-4">{user.auth_info.name}</h3>
                  <span style={{
                    background: '#fef3c7',
                    color: '#92400e',
                    padding: '4px 12px',
                    borderRadius: 100,
                    fontSize: 12,
                    fontWeight: 600
                  }}>Pending</span>
                </div>

                <p className="body-small">{user.auth_info.email}</p>
                <p className="body-small" style={{ color: 'var(--text-muted)', marginTop: 8 }}>
                  Registered: {new Date(user.auth_info.created_at).toLocaleDateString()}
                </p>

                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <button
                    onClick={() => onViewDetails(user.auth_info.user_id)}
                    className="btn-primary"
                    style={{ flex: 1 }}
                  >
                    Review & Approve
                  </button>

                  <button
                    onClick={() => openCoachRules(user)}
                    className="btn-secondary"
                    style={{ flex: 1 }}
                  >
                    Coach Rules
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Approved Users Section */}
      <h2 className="heading-3" style={{ marginBottom: 16 }}>
        Active Users ({approvedUsers.length})
      </h2>

      <div className="admin-users-grid">
        {approvedUsers.map(user => (
          <div key={user.auth_info.user_id} className="admin-user-card">
            <div className="admin-user-header">
              <h3 className="heading-4">{user.auth_info.name}</h3>
              {user.auth_info.is_admin && <span className="admin-badge">Admin</span>}
              {user.auth_info.is_trainer && <span className="admin-badge" style={{ marginLeft: 8 }}>Trainer</span>}
            </div>

            <p className="body-small">{user.auth_info.email}</p>

            {user.profile && (
              <div className="admin-user-stats">
                <div className="stat-row">
                  <span>Weight:</span>
                  <span>{user.profile.weight} lbs → {user.profile.goal_weight} lbs</span>
                </div>
                <div className="stat-row">
                  <span>Daily Target:</span>
                  <span>{user.profile.daily_target} cal</span>
                </div>
              </div>
            )}

            <div className="admin-today-stats">
              <h4 className="body-medium" style={{ marginBottom: 8 }}>Today</h4>
              <div className="stat-row">
                <span>Consumed:</span>
                <span>{user.today_stats.calories_consumed.toFixed(0)} cal</span>
              </div>
              <div className="stat-row">
                <span>Burned:</span>
                <span>{user.today_stats.calories_burned.toFixed(0)} cal</span>
              </div>
              <div className="stat-row">
                <span>Net:</span>
                <span style={{ fontWeight: 600 }}>{user.today_stats.net_calories.toFixed(0)} cal</span>
              </div>
            </div>

            <div style={{ display: 'grid', gap: 8, marginTop: 16 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => onViewDetails(user.auth_info.user_id)}
                  className="btn-secondary"
                  style={{ flex: 1 }}
                >
                  View Details
                </button>

                <button
                  onClick={() => openCoachRules(user)}
                  className="btn-secondary"
                  style={{ flex: 1 }}
                >
                  Coach Rules
                </button>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                {!isTrainerUser(user) ? (
                  <button
                    onClick={() => makeTrainer(user.auth_info.user_id)}
                    className="btn-secondary"
                    style={{ flex: 1 }}
                  >
                    🧑‍🏫 Make Trainer
                  </button>
                ) : (
                  <button
                    onClick={() => revokeTrainer(user.auth_info.user_id)}
                    className="btn-secondary"
                    style={{ flex: 1, color: '#dc2626', borderColor: '#dc2626' }}
                  >
                    Remove Trainer
                  </button>
                )}

                <button
                  onClick={openAssignClients}
                  className="btn-secondary"
                  style={{ flex: 1 }}
                >
                  Assign Clients
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Assign Clients Modal (unchanged) */}
      {showAssignModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: 16
          }}
          onMouseDown={closeAssignClients}
        >
          <div
            style={{
              background: '#fff',
              color: '#111827',
              width: '100%',
              maxWidth: 820,
              borderRadius: 16,
              boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
              overflow: 'hidden'
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: 16,
              borderBottom: '1px solid rgba(0,0,0,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12
            }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>Assign Clients to Trainer</div>
                <div style={{ fontSize: 13, color: '#6B7280' }}>
                  Pick a trainer, then select clients to assign.
                </div>
              </div>

              <button className="btn-secondary" type="button" onClick={closeAssignClients} disabled={assignLoading}>
                Close
              </button>
            </div>

            <div style={{ padding: 16, maxHeight: '70vh', overflowY: 'auto' }}>
              <div className="form-group">
                <label className="body-medium">Trainer</label>
                <select
                  className="form-input"
                  value={assignTrainerId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setAssignTrainerId(id);
                    setAssignClientIds([]);
                    if (id) loadAssignmentsForTrainer(id);
                  }}
                >
                  <option value="">Select a trainer...</option>
                  {trainers.map(t => (
                    <option key={t.user_id} value={t.user_id}>
                      {t.name} ({t.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="body-medium">Clients</label>

                {!assignTrainerId ? (
                  <div className="body-small" style={{ color: '#6B7280' }}>
                    Select a trainer first.
                  </div>
                ) : (
                  <div style={{
                    border: '1px solid rgba(0,0,0,0.12)',
                    borderRadius: 12,
                    padding: 12,
                    display: 'grid',
                    gap: 8
                  }}>
                    {clientCandidates.map(c => {
                      const checked = assignClientIds.includes(c.user_id);
                      return (
                        <label
                          key={c.user_id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: 10,
                            borderRadius: 10,
                            background: checked ? 'rgba(0,0,0,0.04)' : 'transparent',
                            cursor: 'pointer'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const on = e.target.checked;
                              setAssignClientIds(prev => {
                                if (on) return Array.from(new Set([...prev, c.user_id]));
                                return prev.filter(x => x !== c.user_id);
                              });
                            }}
                          />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ fontWeight: 700 }}>{c.name || 'Unnamed'}</div>
                            <div style={{ fontSize: 12, color: '#6B7280' }}>{c.email}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div style={{
              padding: 16,
              borderTop: '1px solid rgba(0,0,0,0.08)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 10
            }}>
              <button className="btn-secondary" type="button" onClick={closeAssignClients} disabled={assignLoading}>
                Cancel
              </button>
              <button className="btn-primary" type="button" onClick={saveAssignmentsForTrainer} disabled={assignLoading || !assignTrainerId}>
                {assignLoading ? 'Saving…' : 'Save Assignments'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Reusable Coach Rules Modal */}
      <CoachRulesModal
        isOpen={showCoachRules}
        onClose={closeCoachRules}
        token={token}
        userId={coachRulesUser?.auth_info?.user_id || ""}
        titleName={coachRulesUser?.auth_info?.name || "User"}
        readOnly={false}
        showToast={showToast}
      />
    </div>
  );
}

export default AdminDashboard;
